import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import type { SiteSetting } from '@/types'

export default function Footer() {
  const [footerSettings, setFooterSettings] = useState<Record<string, string>>({})

  useEffect(() => {
    const fetchFooter = async () => {
      try {
        const [footerRes, seoRes, logoRes, titleRes] = await Promise.all([
          lumi.entities.site_settings.list({ filter: { category: 'footer' }, limit: 30 }),
          lumi.entities.site_settings.list({ filter: { category: 'seo' }, limit: 10 }),
          lumi.entities.site_settings.list({ filter: { key: 'logo_image' }, limit: 1 }),
          lumi.entities.site_settings.list({ filter: { key: 'site_title' }, limit: 1 }),
        ])
        const map: Record<string, string> = {}
        ;(footerRes.list as SiteSetting[]).forEach(s => { map[s.key] = s.value })
        ;(seoRes.list as SiteSetting[]).forEach(s => { map[s.key] = s.value })
        if (logoRes.list[0])  map['logo_image']  = (logoRes.list[0] as SiteSetting).value
        if (titleRes.list[0]) map['site_title']  = (titleRes.list[0] as SiteSetting).value
        setFooterSettings(map)
      } catch (e) {
        console.error('Erro ao carregar rodapé:', e)
      }
    }
    fetchFooter()
  }, [])

  const year            = new Date().getFullYear()
  const siteName        = footerSettings['site_title']             || 'PALACIO DA SABEDORIA'
  const institutionName = footerSettings['footer_institution_name']|| ''
  const cnpj            = footerSettings['footer_cnpj']            || ''
  const logoImage       = footerSettings['logo_image']             || ''
  const contact         = footerSettings['footer_contact']         || ''
  const copyright       = footerSettings['footer_copyright']       || `© ${year} ${siteName}. Todos os direitos reservados.`
  const about           = footerSettings['footer_about']           || ''
  const privacyLink     = footerSettings['footer_privacy_link']    || '#'
  const termsLink       = footerSettings['footer_terms_link']      || '#'

  return (
    <footer className="w-full mt-0 border-t" style={{ background: '#1E3A5F', borderColor: '#1e3a5f' }}>
      <div className="h-1 w-full" style={{ background: '#1A56DB' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-7 pb-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-7 mb-5">

          {/* ── Brand ── */}
          <div>
            <Link to="/" className="inline-flex items-center gap-2.5 mb-4 group">
              {logoImage ? (
                <img
                  src={logoImage}
                  alt={siteName}
                  className="h-10 w-auto object-contain max-w-[160px] opacity-90 group-hover:opacity-100 transition-opacity"
                />
              ) : (
                <>
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-sm transition-all group-hover:scale-105"
                    style={{ background: '#1A56DB' }}
                  >
                    <span className="text-white font-black text-sm tracking-tight">PS</span>
                  </div>
                  <span
                    className="font-bold text-white text-base tracking-tight transition-colors group-hover:text-[#93b4f8]"
                  >
                    {siteName}
                  </span>
                </>
              )}
            </Link>
            {about && (
              <p className="text-sm leading-relaxed" style={{ color: '#9ca3af' }}>{about}</p>
            )}
            {(institutionName || cnpj) && (
              <div className="mt-3 pt-3 border-t" style={{ borderColor: '#243f6a' }}>
                {institutionName && (
                  <p className="text-xs font-semibold" style={{ color: '#9ca3af' }}>{institutionName}</p>
                )}
                {cnpj && (
                  <p className="text-xs" style={{ color: '#6b7280' }}>CNPJ: {cnpj}</p>
                )}
              </div>
            )}
          </div>

          {/* ── Contato ── */}
          {contact && (
            <div>
              <h3 className="font-bold mb-4 text-sm uppercase tracking-widest flex items-center gap-2" style={{ color: '#fff' }}>
                <span className="w-4 h-0.5 rounded-full inline-block" style={{ background: '#1A56DB' }} />
                Contato
              </h3>
              <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#9ca3af' }}>{contact}</p>
            </div>
          )}

          {/* ── Links ── */}
          <div>
            <h3 className="font-bold mb-4 text-sm uppercase tracking-widest flex items-center gap-2" style={{ color: '#fff' }}>
              <span className="w-4 h-0.5 rounded-full inline-block" style={{ background: '#1A56DB' }} />
              Links
            </h3>
            <ul className="space-y-2.5">
              {[
                { href: privacyLink, label: 'Política de Privacidade' },
                { href: termsLink,   label: 'Termos de Uso' },
                { href: '/admin/login', label: 'Área Administrativa' },
              ].map(({ href, label }) => (
                <li key={label}>
                  <a
                    href={href}
                    className="text-sm inline-flex items-center gap-1.5 group transition-colors"
                    style={{ color: '#9ca3af' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#93b4f8')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
                  >
                    <span className="w-1 h-1 rounded-full flex-shrink-0 transition-colors" style={{ background: '#4b5563' }} />
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── Rodapé inferior ── */}
        <div className="border-t pt-4 flex flex-col sm:flex-row items-center justify-between gap-2" style={{ borderColor: '#243f6a' }}>
          <p className="text-xs" style={{ color: '#6b7280' }}>{copyright}</p>
          <p className="text-xs" style={{ color: '#4b5563' }}>
            Desenvolvido por{' '}
            <span style={{ color: '#1A56DB' }} aria-label="Railson Abreu">Railson Abreu</span>
          </p>
        </div>
      </div>
    </footer>
  )
}
